// ============================================
// ModalVincularCliente.js
// ============================================
// Permite vincular o cambiar el cliente de un expediente sin tocar el resto
// de los campos. Reusa SelectorClienteExpediente para mantener la jerarquia
// obligatoria grupo -> razon -> sucursal.
//
// Caso de uso: el vendedor crea el expediente en la calle sin cliente y lo
// vincula despues, o cambia de sucursal cuando el contacto correcto resulta
// pertenecer a otra ubicacion.

import React, { useEffect, useMemo, useState } from 'react';
import {
  FF, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CL, COLOR_BORDE,
  API_BASE_CLI, authHeaders,
  ShellModal, ToastFlotante, botonSecundario, botonPrimario,
} from './helpersExpedientes';
import { useUsuario } from '../../context/UsuarioContext';
import SelectorClienteExpediente from '../SelectorClienteExpediente';

function detalleDesdeExpediente(exp) {
  if (!exp) return null;
  const d = {};
  if (exp.cliente_grupo_id) {
    d.grupo = { id: exp.cliente_grupo_id, nombre: exp.cliente_grupo_nombre || '' };
  }
  if (exp.cliente_razon_social_id) {
    d.razon_social = {
      id: exp.cliente_razon_social_id,
      razon_social: exp.cliente_razon_social_nombre || '',
    };
  }
  if (exp.cliente_sucursal_id) {
    d.sucursal = {
      id: exp.cliente_sucursal_id,
      nombre_interno: exp.cliente_sucursal_nombre || '',
      ciudad: exp.cliente_sucursal_ciudad || null,
    };
  }
  if (exp.cliente_persona_id) {
    d.persona = {
      id: exp.cliente_persona_id,
      nombre_completo: exp.cliente_persona_nombre || '',
    };
  }
  return Object.keys(d).length > 0 ? d : null;
}

export default function ModalVincularCliente({ expediente, onCerrar, onVinculado, esCelular }) {
  const { usuario } = useUsuario();

  const [cliente, setCliente] = useState(() => ({
    cliente_id: expediente?.cliente_id || null,
    cliente_grupo_id: expediente?.cliente_grupo_id || null,
    cliente_razon_social_id: expediente?.cliente_razon_social_id || null,
    cliente_sucursal_id: expediente?.cliente_sucursal_id || null,
    cliente_persona_id: expediente?.cliente_persona_id || null,
    _resumen: expediente?.cliente_jerarquia_corta || '',
    _detalle: detalleDesdeExpediente(expediente),
  }));
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !enviando) onCerrar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enviando, onCerrar]);

  const sucursalFaltante = !!cliente.cliente_grupo_id && !cliente.cliente_sucursal_id;

  const cambios = useMemo(() => {
    const diff = {};
    if ((cliente.cliente_grupo_id || null) !== (expediente?.cliente_grupo_id || null)) {
      diff.cliente_grupo_id = cliente.cliente_grupo_id || null;
    }
    if ((cliente.cliente_razon_social_id || null) !== (expediente?.cliente_razon_social_id || null)) {
      diff.cliente_razon_social_id = cliente.cliente_razon_social_id || null;
    }
    if ((cliente.cliente_sucursal_id || null) !== (expediente?.cliente_sucursal_id || null)) {
      diff.cliente_sucursal_id = cliente.cliente_sucursal_id || null;
    }
    if ((cliente.cliente_persona_id || null) !== (expediente?.cliente_persona_id || null)) {
      diff.cliente_persona_id = cliente.cliente_persona_id || null;
    }
    return diff;
  }, [cliente, expediente]);

  const hayCambios = Object.keys(cambios).length > 0;

  const guardar = async () => {
    if (!hayCambios || enviando) return;
    if (sucursalFaltante) {
      setError('Si vinculas un cliente, la sucursal es obligatoria.');
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/oportunidades/${expediente.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ...cambios, usuario_id: usuario?.id || null }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401 || r.status === 403) {
        setError('No tienes permisos para vincular cliente en este expediente.');
        return;
      }
      if (!r.ok) throw new Error(data?.error || `Error ${r.status}`);
      setToast({ tipo: 'ok', msg: 'Cliente vinculado.' });
      setTimeout(() => onVinculado(data), 700);
    } catch (e) {
      setError(e.message || 'No se pudo vincular el cliente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <ShellModal onClose={enviando ? () => {} : onCerrar} ancho={620} esCelular={esCelular}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${COLOR_BORDE}`,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COLOR_OSCURO, fontFamily: FF }}>
              Vincular cliente
            </h2>
            <div style={{ marginTop: 4, fontSize: 12, color: COLOR_GRIS_CL }}>
              {expediente.folio}
            </div>
          </div>
          <button
            onClick={onCerrar}
            disabled={enviando}
            aria-label="Cerrar"
            style={{
              background: 'transparent', border: 'none',
              cursor: enviando ? 'not-allowed' : 'pointer',
              color: COLOR_GRIS_CL, fontSize: 22, padding: 4, fontFamily: FF,
            }}
          >{'×'}</button>
        </div>

        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
          <div style={{ fontSize: 13, color: COLOR_GRIS, lineHeight: 1.5 }}>
            Selecciona el grupo, la razon social y la sucursal a la que pertenece este
            expediente. La direccion del proyecto se hereda de la sucursal elegida.
          </div>
          <SelectorClienteExpediente
            valor={cliente}
            onChange={setCliente}
            esCelular={esCelular}
          />
          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
              padding: '8px 12px', borderRadius: 8, fontSize: 12,
            }}>{error}</div>
          )}
        </div>

        <div style={{
          padding: 16, borderTop: `1px solid ${COLOR_BORDE}`, background: '#fafafa',
          display: 'flex', justifyContent: 'space-between', gap: 10,
        }}>
          <button onClick={onCerrar} disabled={enviando} style={botonSecundario}>Cancelar</button>
          <button
            onClick={guardar}
            disabled={!hayCambios || enviando || sucursalFaltante}
            style={{
              ...botonPrimario,
              opacity: (!hayCambios || enviando || sucursalFaltante) ? 0.5 : 1,
              cursor: (!hayCambios || enviando || sucursalFaltante) ? 'not-allowed' : 'pointer',
            }}
          >{enviando ? 'Guardando...' : 'Vincular cliente'}</button>
        </div>
      </ShellModal>
      {toast && <ToastFlotante tipo={toast.tipo}>{toast.msg}</ToastFlotante>}
    </>
  );
}
